import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DownloadPageSize, DropdownStatusList, FilterPaymentTypeOptions, FormTypeEdit, PageSize, PaymentTypeOptions, ReturnConditionOptions, TransactionTypeRental, TransactionTypeSales } from "utils/Constant";
import Dropdown from "components/Input/Dropdown";
import { DateLong, DateShort, GetStatus, money } from "utils/helpers"
import { useModal } from "context/ModalContext";
import DatePicker from "components/Input/DatePicker";
import { FilterCard, FormCard, MainBodyCard, PagesTitle } from "components/Cards";
import Pagination from "components/Input/PaginationButton";
import { SalesOrderService } from "services/SalesOrderService/SalesOrderService";
import { RentalOrderService } from "services/RentalOrderService/RentalOrderService";
import GridDeleteButton from "components/Button/GridDeleteButton";
import DataTable from "components/DataTable";
import { exportSalesOrderToPdf } from "utils/export/ExportSales";
import PrintButton from "components/Button/PrintButton";

export default function TransactionSales() {
    let navigate = useNavigate()

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchRentalOrderList, fetchReturnRental, fetchDeliverRental } = RentalOrderService()
    const { fetchSalesOrderList, fetchDeleteSalesOrder } = SalesOrderService()

    const [orderList, setOrderList] = useState([])
    const [customerName, setCustomerName] = useState('')
    const [rentalStatus, setRentalStatus] = useState('')
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [transactionType, setTransactionType] = useState('1')
    const [selectedRentalReturn, setSelectedRentalReturn] = useState({})
    const [returnCondition, setReturnCondition] = useState('')
    const [returnNotes, setReturnNotes] = useState('')
    const [returnDate, setReturnDate] = useState(new Date())
    const [showModalRentalReturn, setShowModalRentalReturn] = useState(false)
    const [orderDateFrom, setOrderDateFrom] = useState(new Date())
    const [orderDateTo, setOrderDateTo] = useState(new Date())
    const [paymentType, setPaymentType] = useState(FilterPaymentTypeOptions[0].value)
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center",
        },
        {
            key: "orderId",
            header: "Order Id",
        },
        {
            key: "orderDate",
            header: "Date",
            render: (row) => DateLong(row.orderDate),
        },
        {
            key: "paymentType",
            header: "Payment Type",
            render: (row) =>
                PaymentTypeOptions.find(
                    (x) => x.value === String(row.paymentType)
                )?.label,
        },
        {
            key: "totalItem",
            header: "Total Items",
        },
        {
            key: "nettAmount",
            header: "Amount",
            render: (row) => `Rp ${money(row.nettAmount)}`,
        },
        {
            key: "profit",
            header: "Profit",
            render: (row) => `Rp ${money(row.profit)}`,
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <GridDeleteButton
                    isActive={row.status}
                    onClick={(e) => {
                        e.stopPropagation();
                        deleteBtnClick(row.orderId, row.status);
                    }}
                    size='w-3'
                />
            ),
        },
    ]

    // Button Event
    const deleteBtnClick = async (id, status) => {
        const onConfirm = await confirm()

        if (!onConfirm) return

        showLoading()

        try {
            const res = await fetchDeleteSalesOrder(id, status)
            if (!res) return

            handlePageChange(0)
        }
        finally {
            hideLoading()
        }
    }
    const transactionClick = (id) => {
        navigate("/transaction-detail?orderId=" + id + "&transactionType=" + transactionType + "&formType=" + FormTypeEdit)
    }

    const exportPdfBtnClick = async () => {
        showLoading()

        try {
            const res = await fetchSalesOrderList(orderDateFrom, orderDateTo, paymentType, status, DownloadPageSize, 0)
            if (!res) return

            exportSalesOrderToPdf(res?.data?.listData ?? [], {
                totalAmount: res?.data?.totalAmount,
                totalProfit: res?.data?.totalProfit,
                dateFrom: orderDateFrom,
                dateTo: orderDateTo,
            });
        }
        finally {
            hideLoading()
        }
    };

    const rentalReturnBtnClick = async (id) => {
        showLoading()

        try {
            let res = await fetchReturnRental(id, returnCondition, returnNotes, returnDate)
            if (!res) return

            handlePageChange(0)
        }
        finally {
            hideLoading()
        }
    }

    // Function Grid
    const handlePageChange = async (newIndex) => {
        const res = await fetchSalesOrderList(orderDateFrom, orderDateTo, paymentType, status, PageSize, newIndex)
        if (!res) return

        const totalData = res?.data?.totalData ?? 0;
        const total = Math.max(1, Math.ceil(totalData / PageSize))

        setOrderList(res)
        setTotalPages(total)
        setPageIndex(newIndex)
    }

    useEffect(() => {
        const onLoad = async () => {
            showLoading()

            try {
                await handlePageChange(0)
            }
            finally {
                hideLoading()
            }
        }

        onLoad()
    }, [orderDateFrom, orderDateTo, paymentType, status, transactionType, rentalStatus, customerName])

    return (
        <MainBodyCard>
            <PagesTitle title='Penjualan' />

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full mt-4">
                <FilterCard title='Date From' width='w-full sm:w-56'>
                    <DatePicker value={orderDateFrom} onChange={setOrderDateFrom} disableClock={true} />
                </FilterCard>

                <FilterCard title='Date To' width='w-full sm:w-56'>
                    <DatePicker value={orderDateTo} onChange={setOrderDateTo} disableClock={true} />
                </FilterCard>

                <FilterCard title='Payment Type' width='w-full sm:w-48'>
                    <Dropdown value={paymentType} options={FilterPaymentTypeOptions} onChange={(e) => { setPaymentType(e.value) }} placeholder='All' />
                </FilterCard>

                <FilterCard title='Status' width='w-full sm:w-36'>
                    <Dropdown value={status} options={DropdownStatusList} onChange={(e) => { setStatus(e.value) }} placeholder='All' />
                </FilterCard>
                <div className="flex items-end">
                    <PrintButton
                        className="px-4 py-2 rounded-full border bg-white hover:bg-slate-100 whitespace-nowrap"
                        onClick={exportPdfBtnClick}
                    >
                        Export PDF
                    </PrintButton>
                </div>
            </div>

            <div className="hidden sm:block mt-5">
                <DataTable
                    columns={columns}
                    data={orderList?.data?.listData ?? []}
                    onRowClick={(row) => transactionClick(row.orderId)}
                    footer={
                        <tr className="h-10">
                            <td colSpan={5} className="text-right px-2">
                                <span className="font-semibold">Total</span>
                            </td>
                            <td colSpan={1} className="px-2">
                                <span className="font-semibold">
                                    Rp {money(String(orderList?.data?.totalAmount))}
                                </span>
                            </td>
                            <td colSpan={3} className="px-2">
                                <span className="font-semibold">
                                    Rp {money(String(orderList?.data?.totalProfit))}
                                </span>
                            </td>
                        </tr>
                    }
                />
            </div>

            <div className="sm:hidden mt-4 flex flex-col gap-3">
                {
                    orderList?.data?.listData?.length === 0 ? (
                        <div className="text-center h-12">
                            <span>No Data Available</span>
                        </div>
                    ) :

                        orderList?.data?.listData?.map((data, i) => {
                            return (
                                <div className="p-2 border rounded-lg hover:bg-slate-100" key={i} onClick={() => { transactionClick(data.orderId) }}>
                                    <div className="flex justify-between">
                                        <span>#{data.orderId} </span>
                                        <span>{DateShort(data.orderDate)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>{data.totalItem} Items</span>
                                        <span>Rp {money(data.nettAmount)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span></span>
                                        <span>{PaymentTypeOptions.find((x) => x.value === String(data.paymentType))?.label}</span>
                                    </div>
                                </div>
                            )
                        })
                }
            </div>

            <Pagination
                pageIndex={pageIndex}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />

            {/* MODAL RENTAL RETURN */}
            {showModalRentalReturn && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">

                    {/* BACKDROP */}
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setShowModalRentalReturn(false)}
                    />

                    {/* MODAL BOX */}
                    <div className="relative overflow-auto bg-white rounded-xl shadow-lg lg:w-9/12 xl:w-8/12 2xl:w-6/12 p-6 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-semibold mb-3">
                            Rental Item Return
                        </h2>

                        <div className="flex flex-col gap-3">
                            <FormCard title='Customer'>
                                <span>{selectedRentalReturn.customerName}</span>
                            </FormCard>

                            <FormCard title='Item'>
                                <span>{selectedRentalReturn.brandName} - {selectedRentalReturn.productName}</span>
                            </FormCard>

                            <FormCard title='Return Date'>
                                <DatePicker className='' value={returnDate} onChange={setReturnDate} />
                            </FormCard>

                            <FormCard title='Return Condition'>
                                <div className="flex gap-2">
                                    {ReturnConditionOptions.map((data, i) => {
                                        return (
                                            <button className={`rounded-full border px-3 py-1 ${data.value === '' ? 'hidden' : 'block'} ${data.value === returnCondition ? 'bg-black text-white' : 'bg-white text-black'}`}
                                                onClick={() => setReturnCondition(data.value)}
                                            >
                                                {data.label}
                                            </button>
                                        )
                                    })}
                                </div>
                            </FormCard>

                            <FormCard title='Return Notes'>
                                <input className="input" value={returnNotes} onChange={(e) => setReturnNotes(e.target.value)} />
                            </FormCard>

                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                className="px-4 py-2 rounded-full border"
                                onClick={() => setShowModalRentalReturn(false)}
                            >
                                Cancel
                            </button>

                            <button
                                className="px-4 py-2 rounded-full bg-black text-white"
                                onClick={() => {
                                    rentalReturnBtnClick(selectedRentalReturn.rentalOrderId);
                                    setShowModalRentalReturn(false);
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </MainBodyCard>
    )
}
